import API from '../api/api'

const GET_SCHEDULED_INSPECTIONS = 'GET_SCHEDULED_INSPECTIONS'
const DELETE_SCHEDULED_INSPECTIONS = 'DELETE_SCHEDULED_INSPECTIONS'
const GET_INSPECTION_SETUPS = 'GET_INSPECTION_SETUPS'
const DELETE_INSPECTION_SETUPS = 'DELETE_INSPECTION_SETUPS'
const UPDATE_INSPECTION_SETUP = 'UPDATE_INSPECTION_SETUP'
const GET_INSPECTION_TEMPLATES = 'GET_INSPECTION_TEMPLATES'
const DELETE_INSPECTION_TEMPLATES = 'DELETE_INSPECTION_TEMPLATES'
const CREATE_INSPECTION_FROM_TEMPLATE = 'CREATE_INSPECTION_FROM_TEMPLATE'
const SET_INSPECTIONS_PAGINATION = 'SET_INSPECTIONS_PAGINATION'

const initialState = {
  inspections: [],
  setups: [],
  templates: [],
  pagination: {
    currentPage: 1,
    pageSize: 25,
    search: '',
    currentStatus: { id: 1, value: '', text: 'All' },
    currentRange: { startDate: null, endDate: null },
  },
}

export const inspectionsReducer = (state = initialState, action) => {
  switch (action.type) {
    case GET_SCHEDULED_INSPECTIONS:
      return {
        ...state,
        inspections: action.payload,
      }
    case DELETE_SCHEDULED_INSPECTIONS:
      return {
        ...state,
        inspections: state.inspections.filter(
          (inspection) => !action.payload.includes(inspection.id),
        ),
      }
    case GET_INSPECTION_SETUPS:
      return {
        ...state,
        setups: action.payload,
      }
    case DELETE_INSPECTION_SETUPS:
      return {
        ...state,
        setups: state.setups.filter((setup) => !action.payload.includes(setup.id)),
      }
    case UPDATE_INSPECTION_SETUP:
      return {
        ...state,
        setups: state.setups.map((setup) =>
          setup.id === action.payload.id ? action.payload : setup,
        ),
      }
    case GET_INSPECTION_TEMPLATES:
      return {
        ...state,
        templates: action.payload,
      }
    case DELETE_INSPECTION_TEMPLATES:
      return {
        ...state,
        templates: state.templates.filter((template) => !action.payload.includes(template.id)),
      }
    case CREATE_INSPECTION_FROM_TEMPLATE:
      return {
        ...state,
        inspections: [...state.inspections, action.payload],
        setups: [action.payload, ...state.setups],
      }
    case SET_INSPECTIONS_PAGINATION:
      return {
        ...state,
        pagination: { ...state.pagination, ...action.payload },
      }
    default:
      return state
  }
}

// Actions

export const fetchScheduledInspections =
  ({ buildingId, page, limit, search, status, startDate, endDate }) =>
  async (dispatch, getState) => {
    const state = getState()
    const pagination = state.inspections.pagination

    // Use Redux pagination state as defaults when parameters not provided
    const finalPage = page !== undefined ? page : pagination.currentPage
    const finalLimit = limit !== undefined ? limit : pagination.pageSize
    const finalSearch = search !== undefined ? search : pagination.search
    const finalStatus = status !== undefined ? status : pagination.currentStatus.value
    const finalStartDate =
      startDate !== undefined
        ? startDate
        : pagination.currentRange.startDate
          ? pagination.currentRange.startDate.getTime()
          : undefined
    const finalEndDate =
      endDate !== undefined
        ? endDate
        : pagination.currentRange.endDate
          ? pagination.currentRange.endDate.getTime()
          : undefined

    const skip = (finalPage - 1) * finalLimit
    return API.getScheduledInspections(buildingId, {
      skip,
      limit: finalLimit,
      status: finalStatus,
      search: finalSearch,
      startDate: finalStartDate,
      endDate: finalEndDate,
    })
      .then((response) => {
        dispatch({
          type: GET_SCHEDULED_INSPECTIONS,
          payload: response.data.data,
        })

        return Promise.resolve(response.data.total)
      })
      .catch((error) => {
        return Promise.reject(error)
      })
  }

export const bulkDeleteScheduledInspections = (inspectionIds) => async (dispatch) => {
  return API.inspectionsBulkDelete(inspectionIds)
    .then(() => {
      dispatch({
        type: DELETE_SCHEDULED_INSPECTIONS,
        payload: inspectionIds,
      })
    })
    .catch((error) => {
      return Promise.reject(error)
    })
}

export const fetchInspectionSetups =
  ({ buildingId, page = 1, limit = 50, search, recurrenceType }) =>
  async (dispatch) => {
    const skip = (page - 1) * limit
    return API.getInspectionSetups(buildingId, { skip, limit, search, recurrenceType })
      .then((response) => {
        dispatch({
          type: GET_INSPECTION_SETUPS,
          payload: response.data.data,
        })

        return Promise.resolve(response.data.total)
      })
      .catch((error) => {
        return Promise.reject(error)
      })
  }

export const bulkDeleteInspectionSetups = (setupIds) => async (dispatch) => {
  return API.inspectionsBulkDelete(setupIds)
    .then(() => {
      dispatch({
        type: DELETE_INSPECTION_SETUPS,
        payload: setupIds,
      })
    })
    .catch((error) => {
      return Promise.reject(error)
    })
}

export const updateInspectionSetup = (setupId, data) => async (dispatch) => {
  return API.updateInspectionSetup(setupId, data)
    .then((response) => {
      dispatch({
        type: UPDATE_INSPECTION_SETUP,
        payload: response.data,
      })

      return Promise.resolve()
    })
    .catch((error) => {
      return Promise.reject(error)
    })
}

export const fetchInspectionTemplates =
  ({ buildingId, page = 1, limit = 50, search }) =>
  async (dispatch) => {
    const skip = (page - 1) * limit

    return API.getInspectionTemplates(buildingId, { skip, limit, search })
      .then((response) => {
        dispatch({
          type: GET_INSPECTION_TEMPLATES,
          payload: response.data.data,
        })

        return Promise.resolve(response.data.total)
      })
      .catch((error) => {
        return Promise.reject(error)
      })
  }

export const bulkDeleteInspectionTemplates = (templateIds) => async (dispatch) => {
  return API.inspectionsBulkDelete(templateIds)
    .then(() => {
      dispatch({
        type: DELETE_INSPECTION_TEMPLATES,
        payload: templateIds,
      })

      return Promise.resolve()
    })
    .catch((error) => {
      return Promise.reject(error)
    })
}

export const createInspectionFromTemplate = (data) => async (dispatch) => {
  return API.createInspectionFromTemplate(data)
    .then((response) => {
      dispatch({
        type: CREATE_INSPECTION_FROM_TEMPLATE,
        payload: response.data,
      })

      return Promise.resolve()
    })
    .catch((error) => {
      return Promise.reject(error)
    })
}

export const setInspectionsPagination = (paginationData) => ({
  type: SET_INSPECTIONS_PAGINATION,
  payload: paginationData,
})

// Selectors

export const selectInspections = (state) => state.inspections.inspections
export const selectInspectionsPagination = (state) => state.inspections.pagination

export const selectInspectionSetups = (state) => state.inspections.setups

export const selectInspectionTemplates = (state) => state.inspections.templates
